import { css } from '@emotion/react';
import { DashboardWidget, DashboardWidgetFC, useDashboardWidget } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { LocationFilterMenu } from '@frontend/location-filter-menu';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Text, styles } from '@frontend/design-system';
import { useGetPendingRequestsV3InfiniteQuery } from '../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/use-get-pending-requests-v3-infinite-query';
import { ScheduleRequestWidgetList } from '../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/ScheduleRequestList/ScheduleRequestWidgetList/ScheduleRequestWidgetList';
import { useLocationFilterMenu } from './hooks/use-location-filter-menu';

/**
 * @dashboard-widget
 *
 * id: schedule-requests-widget
 * title: Appointment Requests
 * description: Effortlessly manage patient requests with a list view of all requested appointments.
 * icon: calendar-small
 */

export const ScheduleRequestsWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('schedule');
  const { selectedLocationIds } = useAppScopeStore();
  const locationFilterMenuProps = useLocationFilterMenu(selectedLocationIds);
  const { selectedOptions: filteredLocationIds } = locationFilterMenuProps;
  const { currentSize } = useDashboardWidget();
  const isNarrowSize = currentSize === 'large-narrow';

  const infiniteListQuery = useGetPendingRequestsV3InfiniteQuery(filteredLocationIds);
  const totalRequests = infiniteListQuery.data?.pages.flatMap((page) => page.rows).length ?? 0;

  return (
    <DashboardWidget>
      <DashboardWidget.Header>
        {!isNarrowSize && <LocationFilterMenu {...locationFilterMenuProps} showClearAll={false} />}
      </DashboardWidget.Header>
      <DashboardWidget.Content css={containerStyle(isNarrowSize)}>
        <>
          <div css={[styles.flexCenter, countContainerStyle(isNarrowSize)]}>
            <Text weight='bold' css={countTextStyle(isNarrowSize)}>
              {totalRequests}
            </Text>
            {!isNarrowSize && <Text color='light'>{t('Pending Requests')}</Text>}
            {isNarrowSize && (
              <div css={locationFilterMenuContainerStyle}>
                <LocationFilterMenu {...locationFilterMenuProps} showClearAll={false} css={locationFilterMenuStyle} />
              </div>
            )}
          </div>
          <div css={listContainerStyle(isNarrowSize)}>
            {!!totalRequests ? (
              <ScheduleRequestWidgetList
                infiniteListQuery={infiniteListQuery}
                refetchScheduleRequestList={infiniteListQuery.refetch}
                height={'100%'}
              />
            ) : (
              <div css={[styles.flexCenter, { height: '100%', justifyContent: 'center' }]}>
                <Text color='light' size='medium'>
                  {t('No Appointment Requests found')}
                </Text>
              </div>
            )}
          </div>
        </>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

ScheduleRequestsWidget.config = {
  size: {
    extraSmall: 'large-narrow',
    small: 'large-narrow',
    medium: 'large-wide',
    large: 'large-wide',
  },
  feature: 'schedule',
};

const containerStyle = (isNarrowSize = false) =>
  css({
    display: 'flex',
    flexDirection: isNarrowSize ? 'column' : 'row',
    flexGrow: 1,
    overflow: 'hidden',
    gap: isNarrowSize ? theme.spacing(1) : theme.spacing(2),
    marginTop: theme.spacing(1),
    alignItems: 'flex-start',
  });

const countContainerStyle = (isNarrowSize = false) =>
  css({
    display: 'flex',
    flexDirection: isNarrowSize ? 'row' : 'column',
    gap: theme.spacing(1),
    ...(!isNarrowSize && { marginBottom: theme.spacing(1) }),
    ...(isNarrowSize && { justifyContent: 'space-between', width: '100%' }),
  });

const countTextStyle = (isNarrowSize = false) =>
  css({ fontSize: theme.fontSize(isNarrowSize ? 75 : 138), lineHeight: 1 });

const listContainerStyle = (isNarrowSize = false) =>
  css({
    flexGrow: 1,
    background: theme.colors.neutral5,
    padding: theme.spacing(1),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    width: isNarrowSize ? '100%' : 'auto',
  });

const locationFilterMenuStyle = css`
  margin-bottom: ${theme.spacing(1.5)};
`;

const locationFilterMenuContainerStyle = css`
  height: 100%;
  display: flex;
  align-items: flex-end;
`;
